/** If the render loop or the scheduler stopped ticking for this long while visible, reload (SPEC 8). */
export const WATCHDOG_STALL_MS = 60_000;

/**
 * Watches heartbeats (render frame, scheduler tick). Hidden pages are paused on purpose,
 * so the watchdog only judges while the page is visible and after a grace period.
 */
export class Watchdog {
  private timer: ReturnType<typeof setInterval> | null = null;
  private visibleSince = Date.now();
  private lastBeats: number[] = [];
  private strikes = 0;

  constructor(
    private readonly beats: () => number[],
    private readonly clock: () => number = () => Date.now(),
    private readonly onStall: () => void = () => location.reload(),
  ) {}

  start(): void {
    if (this.timer) return;
    this.visibleSince = this.clock();
    document.addEventListener("visibilitychange", this.onVisibility);
    this.timer = setInterval(() => this.check(), 10_000);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  private onVisibility = () => {
    if (!document.hidden) this.visibleSince = this.clock();
  };

  /** For tests. */
  markVisible(at: number): void {
    this.visibleSince = at;
  }

  /**
   * Returns true when a stall was detected. A beat counts as stalled only when it is too old
   * and did not move since the previous check, twice in a row: a clock jump (device sleep)
   * is not a stall, a loop that stopped ticking is.
   */
  check(): boolean {
    if (typeof document !== "undefined" && document.hidden) {
      this.strikes = 0;
      return false;
    }
    const now = this.clock();
    const beats = this.beats();
    const stuck = beats.some((t, i) => now - t > WATCHDOG_STALL_MS && t === this.lastBeats[i]);
    this.lastBeats = beats;
    if (now - this.visibleSince < WATCHDOG_STALL_MS) return false;
    this.strikes = stuck ? this.strikes + 1 : 0;
    if (this.strikes < 2) return false;
    this.strikes = 0;
    this.onStall();
    return true;
  }
}
